import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import { withTranslation } from 'react-i18next';
import { DaInput, SearchIcon, InternalGrid } from '@paygreen/paygreen-ui';
import { useQueryState } from 'react-router-use-location-state';
import {
    timestampToDateWithHour,
    IsBigDesktop,
    fixExternalUrl,
} from '../../../utils/tools';
import {
    statusActivation,
    stripeAccountActivation,
} from '../../../utils/constants';
import { PaginationTable, StatusFilterTable } from '../../../components';
import useTeamMembers from '../hooks/useTeamMembers';
import { accountTeamManagerMap } from '../shared';
import AdminCharitiesDaTable from './AdminCharitiesDaTable';

const AdminCharitiesTable = ({
    t,
    data,
    fetchData,
    isLoading,
    pageCount: controlledPageCount,
    totalItems,
}) => {
    const { teamAMs, teamMembers } = useTeamMembers();
    const [activationFilter, setActivationFilter] = useQueryState(
        'activation',
        '',
    );
    const [accountFilter, setAccountFilter] = useQueryState('account', '');
    const [stripeFilter, setStripeFilter] = useState();
    const [activePageReset, setActivePageReset] = useState(false);
    const [isOnboardingCompleted, setIsOnBoardingCompleted] = useQueryState(
        'isOnboardingCompleted',
        '',
    );
    const [isReadyToOnboard, setIsReadyToOnboard] = useQueryState(
        'readyToOnboard',
        '',
    );
    const [isAssociationValidated, setIsAssociationValidated] = useQueryState(
        'isAssociationValidated',
        '',
    );

    /**
     * @description to apply correct values for each query parameters for stripe account
     * @param {string} value - one of VALIDATED | TOVALIDATE | TOCREATE | NONE
     */
    const handleStripeAccountStatus = useCallback(
        (value) => {
            setStripeFilter(value);

            switch (value) {
                case 'VALIDATED':
                    setIsAssociationValidated('1');
                    setIsOnBoardingCompleted(null);
                    setIsReadyToOnboard(null);
                    break;
                case 'TOVALIDATE':
                    setIsAssociationValidated('0');
                    setIsOnBoardingCompleted('1');
                    setIsReadyToOnboard(null);
                    break;
                case 'TOCREATE':
                    setIsAssociationValidated('0');
                    setIsOnBoardingCompleted('0');
                    setIsReadyToOnboard('1');
                    break;
                case 'NONE':
                    setIsAssociationValidated(null);
                    setIsOnBoardingCompleted('0');
                    setIsReadyToOnboard('0');
                    break;
                default:
                    setIsAssociationValidated(null);
                    setIsOnBoardingCompleted(null);
                    setIsReadyToOnboard(null);
            }
        },
        [
            setIsAssociationValidated,
            setIsReadyToOnboard,
            setIsOnBoardingCompleted,
            setStripeFilter,
        ],
    );

    const columns = useMemo(
        () => [
            {
                Header: t('admin.charities.table.associationName'),
                id: 'association',
                accessor: (d) => [
                    d.association.name,
                    fixExternalUrl(d.association.url),
                    d.association.idAssociation,
                ],
            },
            {
                Header: t('admin.users.table.user'),
                id: 'user',
                accessor: (d) => {
                    const {
                        firstname,
                        lastname,
                        username,
                        contact: { phone, email },
                    } = d.representative;

                    return [firstname, lastname, phone, email, username];
                },
            },
            {
                Header: t('admin.users.table.creationDate'),
                id: 'createdAt',
                accessor: (d) =>
                    timestampToDateWithHour(d.association.createdAt),
            },
            {
                Header: t('admin.users.table.activationDate'),
                id: 'enabledAt',
                accessor: (d) => [d.representative.enabledAt],
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={setActivationFilter}
                        setActivePageReset={setActivePageReset}
                        statusConstants={statusActivation}
                        i18TextBase="admin.users.table.activationStatusFilter"
                        defaultValue={activationFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: t('admin.charities.table.visible'),
                id: 'visible',
                accessor: (d) => d.association.visible,
            },
            {
                Header: t('admin.charities.table.stripeAccount'),
                id: 'stripeAccount',
                accessor: (d) => [
                    d.readyToOnboard,
                    d.onboardingCompleted,
                    d.association.validatedAt,
                ],
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={handleStripeAccountStatus}
                        setActivePageReset={setActivePageReset}
                        statusConstants={stripeAccountActivation}
                        i18TextBase="admin.charities.table.stripeAccountStatusFilter"
                        defaultValue={stripeFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: t('admin.users.table.accountManager'),
                accessor: 'accountManager',
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={setAccountFilter}
                        setActivePageReset={setActivePageReset}
                        manualOptions={[
                            { value: '', text: t('admin.users.table.all') },
                        ].concat(teamAMs.map(accountTeamManagerMap))}
                        i18TextBase="matching.table.accountManager"
                        defaultValue={accountFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: 'Actions',
            },
        ],
        [
            t,
            setActivePageReset,
            teamAMs,
            setAccountFilter,
            setActivationFilter,
            accountFilter,
            stripeFilter,
            activationFilter,
            handleStripeAccountStatus,
        ],
    );

    const defaultColumn = useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: ({ column: { filterValue, setFilter } }) => {
                return (
                    <DaInput
                        value={filterValue || ''}
                        onChange={(e) => {
                            setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
                        }}
                        placeholder={t('admin.users.table.searchInput')}
                        fieldSize="sm"
                        icon={<SearchIcon />}
                    />
                );
            },
        }),
        [t],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageCount,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                pageIndex: 0,
                pageSize: 25,
            },
            manualPagination: true,
            pageCount: controlledPageCount,
            manualSortBy: true,
            autoResetPage: false,
            // we ponctually override table internal state to resetPage index with component SelectColumnFilter when needed
            useControlledState: (state) => {
                if (activePageReset) {
                    return { ...state, pageIndex: 0 };
                } else {
                    return state;
                }
            },
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    useEffect(() => {
        fetchData({
            pageIndex,
            pageSize,
            activationFilter,
            accountFilter,
            isOnboardingCompleted,
            isAssociationValidated,
            isReadyToOnboard,
        });
    }, [
        fetchData,
        pageIndex,
        pageSize,
        activationFilter,
        accountFilter,
        isOnboardingCompleted,
        isAssociationValidated,
        isReadyToOnboard,
    ]);

    return (
        <InternalGrid
            displayType="grid"
            gridGap="md"
            gridTemplateColumns="1fr"
            blockPadding="none"
            justifyItems="stretch"
            hasStaticWidth={IsBigDesktop() ? false : true}
        >
            <AdminCharitiesDaTable
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                prepareRow={prepareRow}
                page={page}
                isLoading={isLoading}
                data={data}
                teamMembers={teamMembers}
            />

            {totalItems > 25 ? (
                <PaginationTable
                    setPageSize={setPageSize}
                    pageCount={pageCount || 1}
                    pageIndex={pageIndex}
                    colorTheme="quaternary"
                    gotoPage={gotoPage}
                    setActivePageReset={setActivePageReset}
                />
            ) : null}
        </InternalGrid>
    );
};

AdminCharitiesTable.propTypes = {
    data: PropTypes.array.isRequired,
    pageCount: PropTypes.number,
    isLoading: PropTypes.bool,
    fetchData: PropTypes.func.isRequired,
    totalItems: PropTypes.number,
};

AdminCharitiesTable.defaultProps = {
    isLoading: false,
    pageCount: 0,
};

export default withTranslation('common')(AdminCharitiesTable);
